import { AvSubsystemAvServiceType, DeviceErrors } from "@minervaproject/avsubsystem-core";
import { useCallback, useContext, useMemo } from "react";

import {
  BarePubsubVirtualClassStateMessageFromClientType,
  PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
  PubsubVirtualUserState,
} from "../shared/types/av-state";

import { isTestEnv } from "./env";
import { isAVEnabled } from "./waffle-flags";

import { serverLowerHand, serverRaiseHand } from "api/api-server-actions";
import { WebsocketContext } from "providers/WebsocketProvider";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectCurrentClassUser,
  selectCurrentClassUserId,
  selectIsClassSessionVirtual,
} from "store/selectors";
import { selectStableIsMainAvTab } from "store/selectors/pubsub-virtual-class-state";

export const useAv = () => {
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const isVirtualClassActive = useAppSelector(selectIsClassSessionVirtual) && isAVEnabled();
  const isMainAvTab = useAppSelector(selectStableIsMainAvTab);

  const isAvActiveForVirtualUser =
    isVirtualClassActive &&
    (currentClassUser?.is_in_class_session || currentClassUser?.is_performing_av_check);
  const isAvActiveInAnotherTab = isAvActiveForVirtualUser && !isMainAvTab;

  return useMemo(() => {
    // NOTE: this is similar to selectCurrentClassUserIsInVirtualClass, but it takes
    // into account the main AV tab and classUser.is_performing_av_check.
    // TODO: rename this to make it more clear that it does additional checks.
    const isInVirtualClass = (isMainAvTab && isAvActiveForVirtualUser) ?? false;
    const isHandRaised = !!currentClassUser?.hand_raised_at;
    const hasClassUser = !!currentClassUser?.id;

    const toggleHand = async () => {
      if (!currentClassUser?.id) {
        console.error("No current virtual user ID");
        return;
      }
      return isHandRaised
        ? serverLowerHand(currentClassUser.id)
        : serverRaiseHand(currentClassUser.id);
    };

    return {
      hasClassUser,
      isAvActiveInAnotherTab,
      isInVirtualClass,
      isVirtualClassActive,
      isHandRaised,
      toggleHand,
    };
  }, [
    isMainAvTab,
    isAvActiveForVirtualUser,
    currentClassUser,
    isAvActiveInAnotherTab,
    isVirtualClassActive,
  ]);
};

export const useAvPubsubMessaging = () => {
  const { sendWebsocketMessage, websocketStatus } = useContext(WebsocketContext);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const classSessionId = useAppSelector(selectActiveClassSessionId);

  const sendPubsubVirtualClassStateMessage = useCallback(
    (message: BarePubsubVirtualClassStateMessageFromClientType) => {
      if (!currentClassUserId) {
        if (!isTestEnv) {
          console.warn("[AvPubsubMessaging] Cannot send message: missing ClassUser");
        }
        return;
      }
      if (websocketStatus !== "connected") {
        if (!isTestEnv) {
          console.warn(
            `[AvPubsubMessaging] Cannot send message: websocket status is '${websocketStatus}'`,
          );
        }
        return;
      }
      sendWebsocketMessage({
        ...message,
        type: PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
        virtual_user_id: currentClassUserId,
        class_session_id: classSessionId,
      });
    },
    [classSessionId, currentClassUserId, websocketStatus, sendWebsocketMessage],
  );

  const sendClassStateUpdate = useCallback(
    (data: PubsubVirtualUserState) => {
      sendPubsubVirtualClassStateMessage({
        action: "update",
        persist: true,
        data: data as Record<string, unknown>,
      });
    },
    [sendPubsubVirtualClassStateMessage],
  );

  const sendObservingJoinMessage = useCallback(() => {
    if (websocketStatus !== "connected") {
      return;
    }
    sendWebsocketMessage({
      type: PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
      action: "join",
      force: false,
      // The monitoring virtual_user_id receives all the updates but is not a participant
      virtual_user_id: `monitoring`,
    });
  }, [sendWebsocketMessage, websocketStatus]);

  return { sendPubsubVirtualClassStateMessage, sendClassStateUpdate, sendObservingJoinMessage };
};

export function deviceErrorForDisplay(error: DeviceErrors | null) {
  const errorToDisplay: { [key: string]: string } = {
    // [DeviceErrors.AUDIO_MAC_NOT_ALLOWED]: "audio_mac_not_allowed",
    [DeviceErrors.AUDIO_WINDOWS_CAMERA_IN_USE]: "Mic In Use by Another App (Windows)",
    [DeviceErrors.AUDIO_NOT_ALLOWED]: "Mic Blocked",
    [DeviceErrors.AUDIO_NOT_FOUND]: "No Mic Detected",
    // [DeviceErrors.AUDIO_FAIL]: "audio_fail",
    // [DeviceErrors.SCREENSHARE_FAIL]: "screenshare_fail",
    // [DeviceErrors.VIDEO_MAC_NOT_ALLOWED]: "video_mac_not_allowed",
    [DeviceErrors.VIDEO_WINDOWS_CAMERA_IN_USE]: "Video In Use by Another App (Windows)",
    [DeviceErrors.VIDEO_NOT_ALLOWED]: "Camera Blocked",
    [DeviceErrors.VIDEO_NOT_FOUND]: "No Camera Detected",
    // [DeviceErrors.VIDEO_FAIL]: "video_fail",
    // [DeviceErrors.MEDIA_TIMEOUT]: "media_timeout",
  };

  if (error === null) {
    return "Unknown error";
  }

  return errorToDisplay[error] || error;
}

export function getAvServiceFormItems() {
  return Object.values(AvSubsystemAvServiceType)
    .filter((value) => typeof value === "string")
    .filter((value) => value !== AvSubsystemAvServiceType.Fake)
    .map((value) => ({
      id: value as string,
      title: value as string,
    }));
}
